import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../components/AuthContext';
import { fetchResumes } from '../services/resumes';

// Does this person have a resume worth calling one?
//
// ── An empty document is not a resume ──────────────────────────────────────
//
// The test is `entryCount > 0`, not "a ResumeDocument row exists". Those are
// different questions, and answering the wrong one is what produced the "My
// Resume" nobody made: reads used to create a document, so a row's existence
// only ever proved somebody had LOOKED at the product. A resume with nothing in
// it also cannot be sent to an employer, which is the thing the rest of the
// site actually needs.
//
// Read through services/resumes.js, which caches for five minutes and is
// invalidated by every write that changes the list — so a person who has just
// built or uploaded one is not told they have not.

const READY = { loading: false, hasResume: true, error: null, answeredFor: null };

export function useHasResume() {
  const { currentUser } = useAuth();
  // The UID, not the user object. An auth context that hands back a fresh
  // object each render — which Firebase's does after any token refresh — would
  // rebuild `check` every render, refire the effect, setState, and render
  // again. That is an infinite loop in front of every page in the product; it
  // crashed the test runner outright the first time this was written.
  const uid = currentUser?.uid || null;
  const [state, setState] = useState({ loading: true, hasResume: false, error: null, answeredFor: null });

  const check = useCallback(async () => {
    if (!uid) {
      // Not signed in is somebody else's decision — PrivateRoute sends them to
      // the login page, and this must not race it to a different answer.
      setState(READY);
      return;
    }
    // Loading is shown ONCE, before the first answer for this person.
    //
    // A recheck — every 'resumes-changed', which the AI sidebar fires when
    // it tailors a copy mid-conversation — used to flip loading back on, and
    // RequireResume swaps the whole page for a spinner while loading. So the
    // gated page unmounted and remounted on every resume-list event, taking
    // its state with it: open groups, a typed-but-unsaved answer, the chat
    // draft (2026-09-04). The last answer stays on screen; the recheck
    // replaces it when it lands.
    setState(s => (s.answeredFor === uid ? s : { ...s, loading: true }));
    try {
      // ── Never block on a stale answer ───────────────────────────────────
      //
      // The cached list is fine for letting somebody THROUGH — a person who
      // had a resume five minutes ago still has one. It is not good enough to
      // keep somebody OUT: adding the first entry to a resume does not
      // invalidate this cache, so a cached "no" would lock a person out of the
      // site for five minutes after they had just built the thing it is asking
      // them for.
      //
      // So: cheap read first, and only pay for a forced one when the cheap
      // answer is about to close the door.
      const has = list => list.some(r => (r.entryCount || 0) > 0);

      let resumes = await fetchResumes();
      if (!has(resumes)) resumes = await fetchResumes({ force: true });

      setState({ loading: false, hasResume: has(resumes), error: null, answeredFor: uid });
    } catch (err) {
      // ── Fails OPEN, deliberately ─────────────────────────────────────────
      //
      // This gate stands in front of the whole product. If a dropped request
      // or a restarting server could close it, one blip locks somebody out of
      // a site they have a perfectly good resume on — and the screen would
      // tell them to go and make the resume they already have.
      //
      // A stranger seeing one page they should not is a smaller harm than a
      // customer being locked out of all of them.
      console.error('[resume-gate] could not check for a resume; letting them through', err);
      setState({ loading: false, hasResume: true, error: err, answeredFor: uid });
    }
  }, [uid]);

  useEffect(() => { check(); }, [check]);

  // Every write that changes the list already fires this, so a resume created
  // in the Studio opens the rest of the site without a reload.
  useEffect(() => {
    const again = () => check();
    window.addEventListener('resumes-changed', again);
    return () => window.removeEventListener('resumes-changed', again);
  }, [check]);

  return { ...state, recheck: check };
}

export default useHasResume;
